"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { OpportunityScoreStartupSelector } from "@/components/dashboard/metrics-games/opportunity-score-startup-selector";
import { TenKStartupSelector } from "@/components/dashboard/metrics-games/tenk-startup-selector";
import { MetricsGamesContent, GAME_TABS } from "@/components/dashboard/metrics-games-content";

type Props = {
  initialGameId: string;
  onClose: () => void;
};

export function MetricsGamesInline({ initialGameId, onClose }: Props) {
  const [activeGameId, setActiveGameId] = useState(initialGameId);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setActiveGameId(initialGameId);
  }, [initialGameId]);

  useEffect(() => {
    sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  return (
    <motion.section
      ref={sectionRef}
      className="metrics-games-inline"
      aria-label="Interactive metrics game"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -80, opacity: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      <p className="metrics-inline-label">Interactive metrics</p>
      <div className="metrics-inline-topbar">
        <div className="metrics-inline-tabs">
          {GAME_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`metrics-inline-tab ${activeGameId === tab.id ? "metrics-inline-tab--active" : ""}`}
              onClick={() => setActiveGameId(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="metrics-inline-actions">
          <Link href="/dashboard/metrics" className="metrics-inline-link">
            Full playground
          </Link>
          <button type="button" className="metrics-inline-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
      </div>

      <div className="metrics-inline-body">
        {activeGameId === "game-6" && (
          <div className="metrics-inline-tenk-selector">
            <TenKStartupSelector />
          </div>
        )}
        {activeGameId === "game-3" && (
          <div className="metrics-inline-tenk-selector">
            <OpportunityScoreStartupSelector />
          </div>
        )}
        <MetricsGamesContent
          activeGameId={activeGameId}
          compact
          tenkSearchInCard={activeGameId === "game-6"}
          opportunityScoreSearchInCard={activeGameId === "game-3"}
          scoreDeltaSearchInCard={activeGameId === "game-4"}
        />
      </div>
    </motion.section>
  );
}
